import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { Upload, X, Building2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import categoriesService, { Category } from '../../services/categories.service';
import countriesService, { Country, Wilaya } from '../../services/countries.service';
import { API_ENDPOINTS } from '../../config/api';

interface TrainingCenterInfo {
  id: number;
  name?: string;
  logo?: string | File;
  phone_number?: string;
  about?: string;
  address?: string;
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  category_id?: string;
  country_id?: number;
  wilaya_id?: number | null;
  city?: string;
}

interface FormErrors {
  name?: string;
  phone_number?: string;
  about?: string;
  address?: string;
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  category_id?: string;
  country_id?: string;
  wilaya_id?: string;
  city?: string;
}

const EditProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trainingCenter, setTrainingCenter] = useState<TrainingCenterInfo | null>(null);
  const [formData, setFormData] = useState<TrainingCenterInfo>({
    id: 0,
    name: '',
    phone_number: '',
    about: '',
    address: '',
    website: '',
    facebook_url: '',
    linkedin_url: '',
    category_id: '',
    country_id: undefined,
    wilaya_id: null,
    city: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [loadingWilayas, setLoadingWilayas] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load training center data, categories, and countries in parallel
        const [data, categoriesData, countriesData] = await Promise.all([
          authService.getTrainingCenterInfo(),
          categoriesService.getCategories(),
          countriesService.getCountries()
        ]);
        
        setCategories(categoriesData);
        setCountries(countriesData);
        setTrainingCenter(data);
        
        // Set form data
        const formDataWithDefaults = {
          ...data,
          wilaya_id: data.wilaya_id || null,
          city: data.city || ''
        };
        setFormData(formDataWithDefaults);

        // Load wilayas if country_id is Algeria (Algeria typically has ID 1, but we'll check by name)
        if (data.country_id) {
          const selectedCountry = countriesData.find(c => c.id === data.country_id);
          if (selectedCountry && selectedCountry.name === 'Algeria') {
            try {
              const algerianWilayas = await countriesService.getWilayas(data.country_id);
              setWilayas(algerianWilayas);
            } catch (error) {
            }
          }
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
        setLoadingCategories(false);
        setLoadingCountries(false);
      }
    };

    loadData();
  }, []);

  // Load wilayas when country changes to Algeria
  useEffect(() => {
    const loadWilayas = async () => {
      if (formData.country_id) {
        const selectedCountry = countries.find(c => c.id === formData.country_id);
        if (selectedCountry && selectedCountry.name === 'Algeria') {
          setLoadingWilayas(true);
          try {
            const algerianWilayas = await countriesService.getWilayas(formData.country_id);
            setWilayas(algerianWilayas);
          } catch (error) {
          } finally {
            setLoadingWilayas(false);
          }
        } else {
          setWilayas([]);
          // Reset wilaya_id if not Algeria
          if (formData.wilaya_id) {
            setFormData(prev => ({ ...prev, wilaya_id: null }));
          }
        }
      } else {
        setWilayas([]);
      }
    };

    loadWilayas();
  }, [formData.country_id, countries]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle country_id change - reset wilaya if changing from Algeria
    if (name === 'country_id') {
      const countryId = value ? parseInt(value) : undefined;
      const selectedCountry = countries.find(c => c.id === countryId);
      setFormData(prev => ({
        ...prev,
        [name]: countryId,
        wilaya_id: selectedCountry?.name === 'Algeria' ? prev.wilaya_id : null,
        city: selectedCountry?.name === 'Algeria' ? (prev.city || '') : prev.city
      }));
    } else if (name === 'wilaya_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (name in errors) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category_id: categoryId
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Check if category is selected
    if (!formData.category_id) {
      newErrors.category_id = 'Veuillez sélectionner une catégorie d\'expertise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Prepare update data - only include logo if a new file is selected
      const updateData: any = {
        name: formData.name,
        phone_number: formData.phone_number,
        about: formData.about,
        address: formData.address,
        website: formData.website || null,
        facebook_url: formData.facebook_url || null,
        linkedin_url: formData.linkedin_url || null,
        category_id: formData.category_id,
        country_id: formData.country_id,
        wilaya_id: formData.wilaya_id || null,
        city: formData.city || null
      };
      
      // Only include logo if a new file is selected
      if (logoFile) {
        updateData.logo = logoFile; // This will be handled by the auth service
      }
      
      await authService.updateTrainingCenter(updateData);
      alert('Profil mis à jour avec succès !');
      navigate('/training-center/dashboard');
    } catch (error) {
      setErrors({ name: 'Échec de la mise à jour du profil. Veuillez réessayer.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Modifier le profil</h1>
              <p className="text-gray-600">Mettez à jour les informations de votre centre de formation</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/training-center/dashboard')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Training Center Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Nom du centre de formation
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Building2 className="inline h-4 w-4 mr-2" />
                Logo du centre (optionnel)
              </label>
              
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <div className="relative">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : trainingCenter?.logo ? (
                    <div className="relative">
                      <img 
                        src={`${API_ENDPOINTS.UPLOADS}/${trainingCenter.logo}`}
                        alt="Current logo" 
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                        <Building2 className="h-8 w-8 text-gray-400" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <Building2 className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-primary/10 file:text-brand-primary hover:file:bg-brand-primary/20"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    PNG, JPG ou GIF jusqu'à 5MB. Recommandé: 400x400px
                  </p>
                  {trainingCenter?.logo && !logoPreview && (
                    <p className="text-xs text-gray-400 mt-1">
                      Logo actuel affiché ci-dessus
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
                Numéro de téléphone
              </label>
              <input
                id="phone_number"
                name="phone_number"
                type="tel"
                value={formData.phone_number || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            {/* About */}
            <div>
              <label htmlFor="about" className="block text-sm font-medium text-gray-700">
                À propos
              </label>
              <textarea
                id="about"
                name="about"
                rows={4}
                value={formData.about || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Parlez-nous de votre centre de formation..."
              />
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Adresse
              </label>
              <textarea
                id="address"
                name="address"
                rows={3}
                value={formData.address || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Ex: 123 Rue de la République, Alger, Algérie"
              />
            </div>

            {/* Location Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <MapPin className="h-4 w-4" />
                Localisation
              </div>

              {/* Country */}
              <div>
                <label htmlFor="country_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Pays
                </label>
                <select
                  id="country_id"
                  name="country_id"
                  value={formData.country_id || ''}
                  onChange={handleInputChange}
                  disabled={loadingCountries}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
                >
                  <option value="">
                    {loadingCountries ? 'Chargement des pays...' : 'Sélectionner un pays'}
                  </option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.country_id && <p className="mt-1 text-sm text-red-600">{errors.country_id}</p>}
              </div>

              {/* Wilaya Selection - Only show if Algeria is selected */}
              {formData.country_id && countries.find(c => c.id === formData.country_id)?.name === 'Algeria' && (
                <div>
                  <label htmlFor="wilaya_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Wilaya
                  </label>
                  <select
                    id="wilaya_id"
                    name="wilaya_id"
                    value={formData.wilaya_id || ''}
                    onChange={handleInputChange}
                    disabled={loadingWilayas}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
                  >
                    <option value="">
                      {loadingWilayas ? 'Chargement des wilayas...' : 'Sélectionner une wilaya'}
                    </option>
                    {wilayas.map((wilaya) => (
                      <option key={wilaya.id} value={wilaya.id}>
                        {wilaya.id.toString().padStart(2, '0')} - {wilaya.name}
                      </option>
                    ))}
                  </select>
                  {errors.wilaya_id && <p className="mt-1 text-sm text-red-600">{errors.wilaya_id}</p>}
                </div>
              )}

              {/* City Input - Only show if not Algeria */}
              {formData.country_id && countries.find(c => c.id === formData.country_id)?.name !== 'Algeria' && (
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    Ville
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Nom de la ville"
                  />
                  {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                </div>
              )}
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                Site web
              </label>
              <input
                id="website"
                name="website"
                type="url"
                value={formData.website || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="https://exemple.com"
              />
            </div>

            {/* Facebook */}
            <div>
              <label htmlFor="facebook_url" className="block text-sm font-medium text-gray-700">
                Facebook
              </label>
              <input
                id="facebook_url"
                name="facebook_url"
                type="url"
                value={formData.facebook_url || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="https://facebook.com/votre-page"
              />
            </div>

            {/* LinkedIn */}
            <div>
              <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-700">
                LinkedIn
              </label>
              <input
                id="linkedin_url"
                name="linkedin_url"
                type="url"
                value={formData.linkedin_url || ''}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="https://linkedin.com/company/votre-entreprise"
              />
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie d'expertise *
              </label>
              {loadingCategories ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-sm text-gray-500">Chargement des catégories...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                  {categories.map(category => (
                    <label key={category.id} className="flex items-start space-x-3 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="category"
                        value={category.id}
                        checked={formData.category_id === category.id}
                        onChange={() => handleCategoryChange(category.id)}
                        className="mt-1 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{category.name_french}</div>
                        <div className="text-xs text-gray-500">{category.name_english}</div>
                      </div>
                    </label>
                  ))}
              </div>
              )}
              {errors.category_id && <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => navigate('/training-center/dashboard')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage; 